import React from 'react';
import { X, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DAY_LABELS = {
  0: 'Today',
  1: 'Tomorrow (1 day)',
  2: '2 days away',
  3: '3 days away',
  4: '4 days away',
  5: '5 days away',
};

const EventReminderModal = ({ isOpen, onClose, bookingsByDays, formatDate }) => {
  const navigate = useNavigate();

  const totalCount = Object.values(bookingsByDays || {}).flat().length;
  if (!isOpen || totalCount === 0) return null;

  const handleViewBookings = () => {
    onClose();
    navigate('/vendor/bookings');
  };

  return (
    <div className="fixed inset-0 z-[10002] overflow-y-auto animate-fade-in">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-all duration-300"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full mx-auto animate-scale-in border border-gray-100"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center ring-4 ring-indigo-50 animate-pulse-slow">
                  <Calendar className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <span className="inline-block px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full mb-1">
                    Event Reminder
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {totalCount} upcoming event{totalCount !== 1 ? 's' : ''} (5th → event day)
                  </h3>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-110"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-700 mb-4">
                Your events from 5 days away through the event day:
              </p>
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {[5, 4, 3, 2, 1, 0].map((days, idx) => {
                  const bookings = bookingsByDays[days] || [];
                  if (bookings.length === 0) return null;
                  return (
                    <div key={days} className="space-y-2 animate-slide-up" style={{ animationDelay: `${idx * 80}ms`, animationFillMode: 'backwards' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
                          {DAY_LABELS[days]}
                        </span>
                        {bookings[0]?.event_date && (
                          <span className="text-xs text-gray-500">({formatDate(bookings[0].event_date)})</span>
                        )}
                      </div>
                      <ul className="space-y-1.5">
                        {bookings.map((booking) => (
                          <li
                            key={booking.id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm transition-all duration-200 hover:bg-indigo-50/70 hover:shadow-sm"
                          >
                            <div>
                              <span className="font-medium text-gray-900">
                                {booking.booking_reference || `#${booking.id?.substring(0, 8)}`}
                              </span>
                              {booking.client?.name && (
                                <span className="text-gray-600 ml-2">– {booking.client.name}</span>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                onClose();
                                navigate(`/vendor/bookings/${booking.id}`);
                              }}
                              className="text-indigo-600 hover:text-indigo-700 font-medium text-xs transition-transform hover:translate-x-0.5"
                            >
                              View
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                Dismiss
              </button>
              <button
                onClick={handleViewBookings}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg"
              >
                View All Bookings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventReminderModal;
